/**
 * Device classes and the UI scale (spec docs/specs/2026-10-08-v2-scale-design.md §3.1).
 *
 * One scale per device class drives every size: `--k-scale` on <html> sets the root
 * font size and every size is rem. Layout follows the design width (viewport ÷ scale).
 *
 * Everything here is pure: callers pass in the window/screen readings, the URL search
 * string, storage and whether the `?device=` / `?ui-scale=` overrides are allowed. The
 * browser reads live in DeviceModeContext (provider + pre-render boot).
 */

export type DeviceClass = 'phone' | 'tablet' | 'kiosk' | 'desktop';
export type Orientation = 'portrait' | 'landscape';
export type UiLayout = 'compact' | 'wide';
/** Legacy `body[data-mode]` / `useDeviceMode()` value. */
export type DeviceMode = 'desktop' | 'mobile' | 'kiosk';

/** All scale values. Tune a single device on site with `?ui-scale=` instead of editing these. */
export const UI_SCALE = {
  phone: 1,
  tablet: 1.15,
  kioskLandscape: 1.25,
  desktopMin: 1,
  desktopMax: 1.1,
  desktopRampFrom: 1440,
  desktopRampTo: 1920,
} as const;

/**
 * Layout breakpoints in design px (CSS px ÷ scale). They equal the container sizes in tokens.css
 * (--container-bp-md 48rem, --container-bp-lg 64rem) because the root font size is exactly
 * 16px × scale; test/scripts/containerLayout.test.ts keeps the two in step.
 */
/** Below this design width the compact (phone-like) layout is used. 48rem (`@bp-md`). */
export const COMPACT_BELOW_DESIGN_PX = 768;

/**
 * Portrait screens (owner, 2026-10-08): every non-phone screen in portrait is laid out this many
 * design px wide — the iPad-portrait look (wide layout: top tabs, nothing at the bottom where a
 * kiosk's on-screen keyboard opens; 6 game columns). Scale = width ÷ 768, kept within 1…2
 * (a kiosk: KIOSK_PORTRAIT_SCALE_MIN…2).
 */
export const PORTRAIT_DESIGN_WIDTH = COMPACT_BELOW_DESIGN_PX;
export const PORTRAIT_SCALE_MAX = 2;
/** From this design width the wide two-column pages (wallet, deposit wizard) apply. 64rem (`@bp-lg`). */
export const BP_LG_DESIGN_PX = 1024;
/** 80rem (`@bp-xl`): room for the side strip's icon card beside a centred page (SideStrip). */
export const BP_XL_DESIGN_PX = 1280;

/** Tablet: touch screen whose short side is at least this and long side at most TABLET_MAX_LONG_SIDE. */
export const TABLET_MIN_SHORT_SIDE = 600;
export const TABLET_MAX_LONG_SIDE = 1400;

/** `?ui-scale=` is clamped to this range. */
export const UI_SCALE_OVERRIDE_MIN = 0.75;
export const UI_SCALE_OVERRIDE_MAX = 2;

/**
 * A portrait kiosk may scale below 1: Windows display scaling shrinks a 1080 × 1920 kiosk to
 * 864 (125 %) or 720 (150 %) CSS px, and the design width must stay 768 (wide layout, top tabs,
 * nothing at the bottom where the touch keyboard opens). Other classes keep the 1…2 clamp.
 */
export const KIOSK_PORTRAIT_SCALE_MIN = UI_SCALE_OVERRIDE_MIN;
export const UI_SCALE_OVERRIDE_KEY = 'ui-scale-override';
export const UI_SCALE_PARAM = 'ui-scale';
export const DEVICE_PARAM = 'device';

const DEVICE_CLASSES: readonly DeviceClass[] = ['phone', 'tablet', 'kiosk', 'desktop'];

export interface DeviceInput {
  /** Layout viewport (window.innerWidth / innerHeight). */
  width: number;
  height: number;
  /** Physical screen in its current orientation; the orientation (and tablet size) source. 0 = unknown. */
  screenWidth: number;
  screenHeight: number;
  /** readIsMobileLayout() */
  isPhone: boolean;
  /** Tablet test only: the primary pointer is a finger (`(pointer: coarse)`), so touch-screen laptops stay desktop. */
  touch: boolean;
  /** The kiosk surface (useSurface; /api/kiosk/info type `kiosk`, VITE_DOMAIN_TYPE or a dev `?surface=`), not a Pages preview host. */
  kioskDomain: boolean;
}

export interface UiScaleState {
  device: DeviceClass;
  orientation: Orientation;
  scale: number;
  /** Viewport width ÷ scale: the width the layout is designed against. */
  designWidth: number;
  /** Window height ÷ scale (shrinks with the on-screen keyboard; the orientation does not). */
  designHeight: number;
  layout: UiLayout;
}

export interface UiScaleOverrides {
  scaleOverride?: number | null;
  deviceOverride?: DeviceClass | null;
}

type ScaleStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * Screen size, preferring the screen. The on-screen keyboard and window resizing change the
 * window, not the screen, so they never flip the class or the orientation.
 */
function screenOrWindow(input: DeviceInput): { width: number; height: number } {
  if (input.screenWidth > 0 && input.screenHeight > 0) {
    return { width: input.screenWidth, height: input.screenHeight };
  }
  return { width: input.width, height: input.height };
}

/**
 * Normalises `screen.width/height` to the current orientation. iOS reports the portrait
 * size whatever the rotation, so trust `screen.orientation.type` when the browser has it.
 */
export function screenSizeFor(
  width: number,
  height: number,
  orientationType: string | undefined
): { width: number; height: number } {
  const long = Math.max(width, height);
  const short = Math.min(width, height);
  if (orientationType?.startsWith('landscape')) return { width: long, height: short };
  if (orientationType?.startsWith('portrait')) return { width: short, height: long };
  return { width, height };
}

/**
 * The kiosk surface decides first: kiosk domains are IP-allowlisted public machines, never a
 * player's phone, and Windows display scaling can make one narrower than the phone check's 768.
 */
export function detectDeviceClass(input: DeviceInput): DeviceClass {
  if (input.kioskDomain) return 'kiosk';
  if (input.isPhone) return 'phone';
  if (input.touch) {
    const { width, height } = screenOrWindow(input);
    if (Math.min(width, height) >= TABLET_MIN_SHORT_SIDE && Math.max(width, height) <= TABLET_MAX_LONG_SIDE) {
      return 'tablet';
    }
  }
  return 'desktop';
}

export function orientationOf(input: DeviceInput): Orientation {
  const { width, height } = screenOrWindow(input);
  return width > height ? 'landscape' : 'portrait';
}

function floorScale(value: number): number {
  return Math.floor(value * 1000) / 1000;
}

function roundScale(value: number): number {
  return Math.round(value * 1000) / 1000;
}

export function scaleFor(device: DeviceClass, orientation: Orientation, width: number): number {
  if (device !== 'phone' && orientation === 'portrait') {
    const min = device === 'kiosk' ? KIOSK_PORTRAIT_SCALE_MIN : 1;
    // Rounded down, so the design width never lands a hair under the wide-layout edge.
    return floorScale(Math.min(PORTRAIT_SCALE_MAX, Math.max(min, width / PORTRAIT_DESIGN_WIDTH)));
  }
  switch (device) {
    case 'phone':
      return UI_SCALE.phone;
    case 'tablet':
      return UI_SCALE.tablet;
    case 'kiosk':
      return UI_SCALE.kioskLandscape; // portrait is handled above
    case 'desktop': {
      const { desktopMin, desktopMax, desktopRampFrom, desktopRampTo } = UI_SCALE;
      if (width <= desktopRampFrom) return desktopMin;
      if (width >= desktopRampTo) return desktopMax;
      const t = (width - desktopRampFrom) / (desktopRampTo - desktopRampFrom);
      return roundScale(desktopMin + (desktopMax - desktopMin) * t);
    }
  }
}

/** A plain positive decimal ("1", "1.4", ".9"), clamped to the override range; anything else → null. */
function parseScaleValue(raw: string | null): number | null {
  if (raw == null) return null;
  const value = raw.trim();
  if (!/^(\d+(\.\d+)?|\.\d+)$/.test(value)) return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return roundScale(Math.min(UI_SCALE_OVERRIDE_MAX, Math.max(UI_SCALE_OVERRIDE_MIN, n)));
}

function safeGet(storage: ScaleStorage | null, key: string): string | null {
  try {
    return storage ? storage.getItem(key) : null;
  } catch {
    return null;
  }
}

function safeSet(storage: ScaleStorage | null, key: string, value: string): void {
  try {
    storage?.setItem(key, value);
  } catch {
    /* private mode / quota */
  }
}

function safeRemove(storage: ScaleStorage | null, key: string): void {
  try {
    storage?.removeItem(key);
  } catch {
    /* private mode */
  }
}

/**
 * Whether `?ui-scale=` may apply: staff tune a kiosk from its own browser, so only the kiosk
 * class, or any class where the dev/preview `?device=` override is allowed (`previewAllowed`).
 * A link shared to a phone or desktop never rescales it.
 */
export function isScaleOverrideAllowed(device: DeviceClass, previewAllowed: boolean): boolean {
  return device === 'kiosk' || previewAllowed;
}

/**
 * On-device scale override. `?ui-scale=<0.75–2>` is stored for this device, `?ui-scale=reset`
 * clears it; with no (or an unusable) param the stored value applies. Bad values are ignored,
 * out-of-range ones clamped, and garbage is never stored.
 *
 * `allowed` (isScaleOverrideAllowed): when false the param is ignored and a stored value is
 * neither applied nor deleted; only `?ui-scale=reset` still works (it only clears).
 */
export function readScaleOverride(search: string, storage: ScaleStorage | null, allowed: boolean): number | null {
  const param = new URLSearchParams(search).get(UI_SCALE_PARAM);
  if (param != null && param.trim().toLowerCase() === 'reset') {
    safeRemove(storage, UI_SCALE_OVERRIDE_KEY);
    return null;
  }
  if (!allowed) return null;
  if (param != null) {
    const fromParam = parseScaleValue(param);
    if (fromParam != null) {
      safeSet(storage, UI_SCALE_OVERRIDE_KEY, String(fromParam));
      return fromParam;
    }
  }
  const stored = safeGet(storage, UI_SCALE_OVERRIDE_KEY);
  if (stored == null) return null;
  const fromStorage = parseScaleValue(stored);
  if (fromStorage == null) safeRemove(storage, UI_SCALE_OVERRIDE_KEY);
  return fromStorage;
}

/** Dev/preview `?device=phone|tablet|kiosk|desktop`; `allowed` = DEV or VITE_UI_PREVIEW. */
export function readDeviceOverride(search: string, allowed: boolean): DeviceClass | null {
  if (!allowed) return null;
  const param = new URLSearchParams(search).get(DEVICE_PARAM)?.trim().toLowerCase();
  return DEVICE_CLASSES.find((c) => c === param) ?? null;
}

export function computeUiScale(input: DeviceInput, opts: UiScaleOverrides = {}): UiScaleState {
  const device = opts.deviceOverride ?? detectDeviceClass(input);
  const orientation = orientationOf(input);
  const scale = opts.scaleOverride ?? scaleFor(device, orientation, input.width);
  const designWidth = input.width / scale;
  const designHeight = input.height / scale;
  // Phones always get the compact layout (a phone on "Desktop site" is data-mode mobile today).
  const layout: UiLayout = device === 'phone' || designWidth < COMPACT_BELOW_DESIGN_PX ? 'compact' : 'wide';
  return { device, orientation, scale, designWidth, designHeight, layout };
}

/** Legacy `data-mode`: phone → mobile, kiosk → kiosk, tablet and desktop → desktop. */
export function deviceModeFor(device: DeviceClass): DeviceMode {
  if (device === 'phone') return 'mobile';
  if (device === 'kiosk') return 'kiosk';
  return 'desktop';
}

export function applyUiScale(state: UiScaleState, html: HTMLElement, body: HTMLElement): void {
  html.style.setProperty('--k-scale', String(state.scale));
  body.dataset.device = state.device;
  body.dataset.orientation = state.orientation;
  body.dataset.layout = state.layout;
}
